/**
 * Pure, framework-neutral match tokenizer for the taxonomy Lookup List.
 *
 * Splits `text` into an ordered list of serializable tokens describing which
 * substrings matched the user's search `query`. The caller (a React
 * presentation component) turns matched tokens into `<mark>` elements — this
 * helper never returns React elements or HTML strings, so it is safe to live
 * under `src/Utils/` per the folder contract (see spec AR-004).
 *
 * Matching is case-insensitive and every occurrence is highlighted. User input
 * is escaped before comparison so regex metacharacters in the query are treated
 * literally and can never throw.
 *
 * @param {string} text  The taxonomy display name to tokenize.
 * @param {string|string[]} query  The search term(s). Empty/whitespace-only
 *   terms are ignored. Terms shorter than `minLength` are ignored.
 * @param {object} [options]
 * @param {number} [options.minLength=1]  Minimum term length to highlight.
 * @returns {Array<{ text: string, matched: boolean }>} Ordered, contiguous
 *   tokens whose concatenated `text` equals the original `text`. An empty
 *   `text` yields `[]`; a text with no match yields a single unmatched token.
 */
/**
 * Escape a string so it is treated literally inside a RegExp.
 * @param {string} value
 * @returns {string}
 */
export function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default function highlightMatch(text, query, options = {}) {
  const { minLength = 1 } = options;

  if (typeof text !== 'string' || text.length === 0) {
    return [];
  }

  const terms = (Array.isArray(query) ? query : [query])
    .filter((term) => typeof term === 'string')
    .map((term) => term.trim())
    .filter((term) => term.length >= minLength && term.length > 0);

  if (terms.length === 0) {
    return [{ text, matched: false }];
  }

  const pattern = terms.map(escapeRegExp).join('|');
  const matcher = new RegExp(pattern, 'gi');

  const tokens = [];
  let lastIndex = 0;
  let match = matcher.exec(text);

  while (match !== null) {
    // Guard against zero-length matches (defensive; escaped terms are non-empty).
    if (match.index === matcher.lastIndex) {
      matcher.lastIndex += 1;
      match = matcher.exec(text);
      // eslint-disable-next-line no-continue
      continue;
    }

    if (match.index > lastIndex) {
      tokens.push({ text: text.slice(lastIndex, match.index), matched: false });
    }
    tokens.push({ text: match[0], matched: true });
    lastIndex = match.index + match[0].length;
    match = matcher.exec(text);
  }

  if (lastIndex < text.length) {
    tokens.push({ text: text.slice(lastIndex), matched: false });
  }

  return tokens;
}
